/**
 * Shared TinyMCE configuration for the admin portal.
 * Image opens Media Library; the native Media dialog (with a custom
 * media_url_resolver) handles external YouTube / Vimeo / X-Twitter embeds.
 */

import { buildTwitterEmbedIframe } from "./twitterEmbedUtils";
import { parseYoutubeVideoId, parseVimeoVideoId } from "./videoEmbedUtils";

export const TINYMCE_SCRIPT_SRC = "/tinymce/tinymce.min.js";

export const DEFAULT_TINYMCE_PLUGINS = [
  "advlist",
  "autolink",
  "link",
  "image",
  "lists",
  "charmap",
  "preview",
  "anchor",
  "searchreplace",
  "wordcount",
  "visualblocks",
  "code",
  "fullscreen",
  "insertdatetime",
  "media",
  "table",
  "emoticons",
  "help",
];

/** Preserves existing toolbar; adds Image (Media Library) and the native Media (embed) button. */
export const DEFAULT_TINYMCE_TOOLBAR =
  "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link xcimage media | preview code fullscreen | emoticons | help";

/**
 * Domains allowed to render un-sandboxed inside inserted media iframes
 * (required for live embeds like YouTube/Vimeo/X-Twitter to actually play/render).
 */
export const TINYMCE_SANDBOX_IFRAMES_EXCLUSIONS = [
  "youtube.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
  "dailymotion.com",
  "embed.music.apple.com",
  "open.spotify.com",
  "giphy.com",
  "dai.ly",
  "codepen.io",
  "platform.twitter.com",
  "twitter.com",
  "x.com",
];

/**
 * Minimal extras so inserted media/embeds survive round-trips without
 * loosening sanitization broadly.
 */
export const TINYMCE_EXTENDED_VALID_ELEMENTS =
  "img[src|alt|title|width|height|style|class]," +
  "video[src|controls|width|height|style|class|preload|poster]," +
  "source[src|type]," +
  "iframe[src|width|height|style|class|frameborder|allow|allowfullscreen|scrolling|name|title]," +
  "blockquote[class|data-media-max-width|lang|dir|cite|style]," +
  "a[href|target|rel|class|title|style]";

/**
 * Resolves an external media URL pasted into TinyMCE's native Media dialog
 * into embeddable iframe HTML. Supports YouTube, Vimeo, and X/Twitter posts.
 * Rejects (without inserting anything) for unsupported URLs.
 */
const resolveMediaEmbedUrl = (data, resolve, reject) => {
  const rawUrl = String(data?.url || "").trim();

  const twitterHtml = buildTwitterEmbedIframe(rawUrl);
  if (twitterHtml) {
    resolve({ html: twitterHtml });
    return;
  }

  const youtubeId = parseYoutubeVideoId(rawUrl);
  if (youtubeId) {
    resolve({
      html:
        `<iframe style="width: 560px; height: 314px; max-width: 100%; border: 0;" ` +
        `src="https://www.youtube.com/embed/${youtubeId}" ` +
        `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ` +
        `allowfullscreen="allowfullscreen"></iframe>`,
    });
    return;
  }

  const vimeoId = parseVimeoVideoId(rawUrl);
  if (vimeoId) {
    resolve({
      html:
        `<iframe style="width: 560px; height: 314px; max-width: 100%; border: 0;" ` +
        `src="https://player.vimeo.com/video/${vimeoId}" ` +
        `allow="autoplay; fullscreen; picture-in-picture" allowfullscreen="allowfullscreen"></iframe>`,
    });
    return;
  }

  reject("No preview available for this URL.");
};

/**
 * @param {object} options
 * @param {number} [options.height=450]
 * @param {() => void} [options.onInsertImage]
 * @param {(callback: Function, value: string, meta: object) => void} [options.filePickerCallback]
 * @param {(editor: object) => void} [options.onSetup]
 */
export const createTinymceInit = ({
  height = 450,
  onInsertImage,
  filePickerCallback,
  onSetup,
} = {}) => ({
  height,
  menubar: false,
  branding: false,
  plugins: DEFAULT_TINYMCE_PLUGINS,
  toolbar: DEFAULT_TINYMCE_TOOLBAR,
  file_picker_types: "image media",
  file_picker_callback:
    typeof filePickerCallback === "function" ? filePickerCallback : undefined,
  extended_valid_elements: TINYMCE_EXTENDED_VALID_ELEMENTS,
  media_live_embeds: true,
  sandbox_iframes: true,
  sandbox_iframes_exclusions: TINYMCE_SANDBOX_IFRAMES_EXCLUSIONS,
  media_url_resolver: resolveMediaEmbedUrl,
  setup: (editor) => {
    editor.ui.registry.addButton("xcimage", {
      icon: "image",
      tooltip: "Insert Image",
      onAction: () => {
        if (typeof onInsertImage === "function") {
          onInsertImage();
        }
      },
    });

    if (typeof onSetup === "function") {
      onSetup(editor);
    }
  },
});
